import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostBinding,
  OnInit,
} from '@angular/core';
import { BehaviorSubject, filter } from 'rxjs';
import { ColorPickerPaletteControl } from '@anecdotes/design-system/form-controls';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  brandingDefaultBgColor,
  brandingDefaultPrimaryColor,
  brandingDefaultTextColor,
  ImageSource,
  TrustCenterBrandingService,
  TrustCenterFacadeService,
  TrustCenterNavigationService,
  urlToImageUrl,
} from '@anecdotes/shared-trust-center';
import { AlertService } from '@anecdotes/design-system/alert';
import { GenericModalsService } from '@anecdotes/design-system/modals';

@Component({
  selector: 'app-branding-settings',
  templateUrl: './branding-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandingSettingsComponent implements OnInit {
  @HostBinding('class')
  private readonly classes = 'flex flex-col w-full h-full bg-white border-l border-navy-40 overflow-hidden';

  readonly sendingRequestBtnLoader$ = new BehaviorSubject<boolean>(false);

  fullLogo: ImageSource;
  favicon: ImageSource;
  primaryColorPickerControl: ColorPickerPaletteControl;
  bgColorPickerControl: ColorPickerPaletteControl;
  textColorPickerControl: ColorPickerPaletteControl;

  wasFormValueChanged = false;

  get enableSaveBtn(): boolean {
    return this.wasFormValueChanged && !!this.fullLogo && !!this.favicon;
  }

  constructor(
    private trustCenterFacadeService: TrustCenterFacadeService,
    private trustCenterBrandingService: TrustCenterBrandingService,
    private alertService: AlertService,
    private genericModals: GenericModalsService,
    private trustCenterNavigationService: TrustCenterNavigationService,
    private cd: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.trustCenterFacadeService
      .getTrustCenterInfo()
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe((trustCenterInfo) => {
        this.fullLogo = urlToImageUrl(trustCenterInfo.full_logo_url);
        this.favicon = urlToImageUrl(trustCenterInfo.favicon_url);

        this.createColorPickerControls();

        this.cd.detectChanges();
      });
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.branding.${key}`;
  }

  handleFullLogoChange(image: ImageSource): void {
    this.fullLogo = image;
    this.wasFormValueChanged = true;
    this.cd.detectChanges();
  }

  handleFaviconChange(image: ImageSource): void {
    this.favicon = image;
    this.wasFormValueChanged = true;
    this.cd.detectChanges();
  }

  onColorChanged(): void {
    this.wasFormValueChanged = true;
  }

  async resetBtnClick(): Promise<void> {
    if (await this.openResetConfirmationModal()) {
      this.primaryColorPickerControl.setValue(brandingDefaultPrimaryColor);
      this.bgColorPickerControl.setValue(brandingDefaultBgColor);
      this.textColorPickerControl.setValue(brandingDefaultTextColor);
      this.cd.detectChanges();

      this.saveSettings(true);
    }
  }

  private openResetConfirmationModal(): Promise<boolean> {
    return this.genericModals.openConfirmationModal({
      confirmTranslationKey: this.buildTranslationKey('resetConfirmationModal.confirmTranslationKey'),
      dismissTranslationKey: this.buildTranslationKey('resetConfirmationModal.dismissTranslationKey'),
      questionTranslationKey: this.buildTranslationKey('resetConfirmationModal.questionTranslationKey'),
      aftermathTranslationKey: this.buildTranslationKey('resetConfirmationModal.aftermathTranslationKey'),
    });
  }

  async saveSettings(reset?: boolean): Promise<void> {
    this.sendingRequestBtnLoader$.next(true);

    try {
      if (reset) {
        this.trustCenterBrandingService.resetBrandingColors();
      } else {
        this.trustCenterBrandingService.initBrandingColors(
          this.primaryColorPickerControl.value,
          this.bgColorPickerControl.value,
          this.textColorPickerControl.value,
        );
      }
      await this.trustCenterFacadeService.updateTrustCenterInfoLogos({
        fullLogo: this.fullLogo,
        favicon: this.favicon,
        primary_color: reset ? null : this.primaryColorPickerControl.value,
        clear_primary_color: reset,
        bg_color: reset ? null : this.bgColorPickerControl.value,
        clear_bg_color: reset,
        text_color: reset ? null : this.textColorPickerControl.value,
        clear_text_color: reset,
      });

      this.wasFormValueChanged = false;
    } catch {
      this.alertService.openActionErrorAlert({
        customBaseTranslationKey: this.buildTranslationKey('tryAgainError'),
      });
    } finally {
      this.sendingRequestBtnLoader$.next(false);
    }
  }

  navigateToPreviewTab(): void {
    this.trustCenterFacadeService.pauseEdit();
    this.trustCenterNavigationService.navigateToPreviewTab(true);
  }

  private createColorPickerControls(): void {
    const { primaryColor, bgColor, textColor } = this.trustCenterBrandingService.getBrandingColors();

    this.primaryColorPickerControl = new ColorPickerPaletteControl({
      initialValue: primaryColor,
    });

    this.bgColorPickerControl = new ColorPickerPaletteControl({
      initialValue: bgColor,
    });

    this.textColorPickerControl = new ColorPickerPaletteControl({
      initialValue: textColor,
    });
  }
}
